/**
 * @fileoverview Mock access token helpers.
 * @description The real backend's access token is an opaque signed JWT; the mock only needs
 * something that round-trips a user id through the Authorization header, so it encodes the id
 * directly into a fake token string rather than a real JWT. There's no mock refresh token: the
 * real refresh token is an httpOnly cookie now, which axios-mock-adapter has no way to simulate
 * (it intercepts before any real network round-trip, so the browser never processes a `Set-Cookie`
 * from it) — silent refresh simply isn't exercisable against this mock. Also reads the Bearer
 * token off a mocked request's config, since the request interceptor (attachAuthToken) has already
 * run by the time the mock adapter sees the request.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import type { AxiosRequestConfig } from 'axios'

const ACCESS_TOKEN_PREFIX = 'mock-access-token'

/**
 * @description Builds a fake access token that encodes the given user id.
 * @param {string} userId - The mock user's id.
 * @returns {string} A fake access token.
 */
export function createMockAccessToken(userId: string): string {
  return `${ACCESS_TOKEN_PREFIX}.${userId}.${Date.now()}`
}

/**
 * @description Recovers the user id encoded into a mock access token.
 * @param {string} token - A token created by createMockAccessToken.
 * @returns {string | null} The encoded user id, or null when the token doesn't look like one of
 * this mock's tokens.
 */
export function extractUserIdFromMockToken(token: string): string | null {
  const [prefix, userId] = token.split('.')
  return prefix === ACCESS_TOKEN_PREFIX ? (userId ?? null) : null
}

/**
 * @description Reads the Authorization header's Bearer token off a mocked request's config.
 * @param {AxiosRequestConfig} config - The request config the mock adapter's handler receives.
 * @returns {string | null} The bearer token, or null when there isn't one.
 */
export function getBearerTokenFromConfig(config: AxiosRequestConfig): string | null {
  const headers = config.headers as unknown

  const raw =
    headers && typeof (headers as { get?: unknown }).get === 'function'
      ? (headers as { get: (name: string) => unknown }).get('Authorization')
      : (headers as Record<string, unknown> | undefined)?.Authorization

  if (typeof raw !== 'string') {
    return null
  }

  const match = /^Bearer (.+)$/.exec(raw)
  return match?.[1] ?? null
}
